import { Box, styled, Typography } from "@material-ui/core";
import type { ReactNode } from "react";
import {
  EPOCUS_BG,
  EPOCUS_BORDER,
  EPOCUS_PANEL_MUTED_BG,
  EPOCUS_PANEL_MUTED_BORDER,
  EPOCUS_PANEL_SCOPED_BORDER,
  EPOCUS_PANEL_SCOPED_HEADER_BG,
  EPOCUS_STATUS_COMPLETE_BG,
  EPOCUS_STATUS_COMPLETE_TEXT,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "./epocus-palette";
import { epocusTheme } from "./epocus-theme-provider";

export type ScopedPanelVariant = "filtered" | "all-time";

const VARIANT_STYLES: Record<
  ScopedPanelVariant,
  { border: string; body: string; header: string }
> = {
  filtered: {
    border: EPOCUS_PANEL_SCOPED_BORDER,
    body: EPOCUS_BG,
    header: EPOCUS_PANEL_SCOPED_HEADER_BG,
  },
  "all-time": {
    border: EPOCUS_PANEL_MUTED_BORDER,
    body: EPOCUS_PANEL_MUTED_BG,
    header: EPOCUS_STATUS_COMPLETE_BG,
  },
};

const Header = styled(Box)({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 14,
  padding: "12px 16px",
  borderBottom: `1px solid ${EPOCUS_BORDER}`,
});

const Body = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 18,
  padding: 14,
  [epocusTheme.breakpoints.up("sm")]: {
    gap: 22,
    padding: 16,
  },
});

const Controls = styled(Box)({
  display: "flex",
  alignItems: "flex-end",
  flexWrap: "wrap",
  gap: 10,
  width: "100%",
  [epocusTheme.breakpoints.up("sm")]: {
    marginLeft: "auto",
    width: "auto",
  },
});

export const AllTimeChip: FC = () => (
  <Box
    style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 8px",
      borderRadius: 6,
      fontSize: rem(10),
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
      backgroundColor: EPOCUS_STATUS_COMPLETE_BG,
      color: EPOCUS_STATUS_COMPLETE_TEXT,
    }}
  >
    All-time
  </Box>
);

export const ScopedPanel: FCwC<{
  variant: ScopedPanelVariant;
  title: string;
  subtitle: string;
  controls?: ReactNode;
}> = ({ variant, title, subtitle, controls, children }) => {
  const styles = VARIANT_STYLES[variant];

  return (
    <Box
      style={{
        border: `1px solid ${styles.border}`,
        borderRadius: 14,
        overflow: "hidden",
        backgroundColor: styles.body,
      }}
    >
      <Header style={{ backgroundColor: styles.header }}>
        <Box>
          <Typography
            style={{
              fontSize: rem(15),
              fontWeight: 700,
              color: EPOCUS_TEXT_PRIMARY,
              lineHeight: 1.25,
            }}
          >
            {title}
          </Typography>
          <Typography
            style={{
              fontSize: rem(12),
              color: EPOCUS_TEXT_SECONDARY,
              marginTop: 2,
            }}
          >
            {subtitle}
          </Typography>
        </Box>
        {controls && <Controls>{controls}</Controls>}
      </Header>
      <Body>{children}</Body>
    </Box>
  );
};
